import { type FormEvent, useMemo, useState } from 'react';
import { supabase } from '../clients/supabaseClient';
import logo from '../assets/logo.png';

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

export default function AccountDeletionRequest() {
    const [email, setEmail] = useState('');
    const [state, setState] = useState<SubmitState>('idle');
    const [message, setMessage] = useState('');

    const normalizedEmail = useMemo(() => email.trim().toLowerCase(), [email]);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!normalizedEmail) {
            setState('error');
            setMessage('Please enter your registered email address.');
            return;
        }

        try {
            setState('submitting');
            setMessage('');

            const { error } = await supabase.functions.invoke('request-account-deletion', {
                body: {
                    email: normalizedEmail,
                },
            });

            if (error) {
                throw error;
            }

            setState('success');
            setMessage(
                'If an account exists for this email address, we have sent a verification email with next steps.'
            );
            setEmail('');
        } catch (error) {
            console.error('Account deletion request failed:', error);
            setState('error');
            setMessage('We could not submit your request right now. Please try again later.');
        }
    };

    return (
        <main className="min-h-screen bg-white px-4 py-10 text-gray-900 sm:py-14">
            <section className="mx-auto grid max-w-5xl overflow-hidden rounded-3xl border border-white/10 bg-white shadow-2xl lg:grid-cols-[0.95fr_1.05fr]">
                <aside className="relative hidden bg-[#161d2b] p-10 text-white lg:block">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(230,24,108,0.38),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.12),transparent_32%)]" />

                    <div className="relative z-10 flex h-full flex-col justify-between">
                        <div>
                            <div className="mb-10 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white p-3 shadow-lg shadow-black/20">
                                <img src={logo} alt="Save Delivery" className="h-full w-full object-contain" />
                            </div>

                            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-pink-200">
                                Save Delivery
                            </p>

                            <h1 className="text-4xl font-bold tracking-tight">
                                Account deletion request
                            </h1>

                            <p className="mt-5 max-w-sm text-sm leading-7 text-slate-200">
                                We respect your privacy. If you no longer use Save Delivery, you can
                                request deletion of your account and associated profile data here.
                            </p>
                        </div>

                        <div className="rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur">
                            <div className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#e6186c]/20 text-[#ff7aad]">
                                ✓
                            </div>

                            <p className="text-sm font-semibold text-white">Secure verification</p>

                            <p className="mt-2 text-sm leading-6 text-slate-200">
                                We send a verification email before notifying our deletion team, so
                                only the account owner can request deletion.
                            </p>
                        </div>
                    </div>
                </aside>

                <div className="bg-white p-6 sm:p-8 lg:p-10">
                    <div className="mb-8 lg:hidden">
                        <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-pink-100 bg-pink-50 p-2 shadow-sm">
                            <img src={logo} alt="Save Delivery" className="h-full w-full object-contain" />
                        </div>

                        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-[#e6186c]">
                            Save Delivery
                        </p>

                        <h1 className="text-3xl font-bold tracking-tight text-[#161d2b]">
                            Account deletion request
                        </h1>
                    </div>

                    <div className="mb-8">
                        <p className="mb-2 text-sm font-semibold text-[#e6186c]">
                            Account & data deletion
                        </p>

                        <h2 className="text-2xl font-bold tracking-tight text-[#161d2b] sm:text-3xl">
                            Request deletion of your account
                        </h2>

                        <p className="mt-4 text-base leading-7 text-gray-600">
                            Use this form if you have uninstalled the app and want to request deletion of
                            your account and associated profile data. To protect your account, we will send
                            a verification email before notifying our deletion team.
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label htmlFor="email" className="mb-1 block text-sm font-medium text-gray-700">
                                Registered email address
                            </label>

                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                                className="h-12 w-full rounded-xl border border-stone-300 px-4 text-[#161d2b] placeholder-gray-400 shadow-sm outline-none transition focus:border-[#e6186c] focus:ring-2 focus:ring-pink-100"
                                placeholder="you@example.com"
                            />
                        </div>

                        <div className="rounded-2xl border border-pink-100 bg-pink-50/70 p-5 text-sm leading-6 text-gray-700">
                            <p className="font-semibold text-[#161d2b]">What happens next?</p>

                            <ol className="mt-3 list-decimal space-y-2 pl-5 marker:text-[#e6186c]">
                                <li>You enter the email address registered to your account.</li>
                                <li>We send a verification email to confirm account ownership.</li>
                                <li>After verification, our deletion team is notified.</li>
                                <li>Developers will review and delete the applicable account data.</li>
                            </ol>
                        </div>

                        {message && (
                            <div
                                role="alert"
                                className={`rounded-2xl border px-4 py-3 text-sm leading-6 ${
                                    state === 'success'
                                        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                                        : 'border-rose-200 bg-rose-50 text-rose-800'
                                }`}
                            >
                                {message}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={state === 'submitting'}
                            aria-busy={state === 'submitting'}
                            className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-[#e6186c] px-5 text-sm font-semibold text-white shadow-sm shadow-pink-200 transition hover:bg-[#d01560] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                        >
                            {state === 'submitting' ? 'Submitting…' : 'Submit deletion request'}
                        </button>
                    </form>

                    <p className="mt-8 border-t border-stone-200 pt-5 text-xs leading-5 text-gray-500">
                        If you did not request account deletion, you can ignore the verification email.
                        Our team is only notified after the registered email address is verified.
                    </p>
                </div>
            </section>
        </main>
    );
}